/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAlignment,
  selectBreakdown,
  selectDateComparison,
  selectSummaryAlignmentToggle,
  selectSymmetricComparisonToggle,
  selectSummaryOtb,
  selectSummaryCustomDate,
  selectIsDetailed,
  selectSummaryEndDate,
  selectSummaryBookingDate,
  selectSummaryStartDate,
  selectSummaryVisualToggle,
  selectPaceVisualIsDisplay,
  selectRevenueSetIsDisplay,
  selectCxlSetIsDisplay,
  selectSymmetricComparisonIsDisplay,
  selectSummaryFilterList,
  selectPaceVisualFilterList,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import {
  selectPaceHorizon,
  selectPaceVariable,
  selectTargetDate,
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectEnableHotelGroups,
  selectSummaryForecastFilter,
  selectReportType,
  selectHotelGroupDropdownList,
  selectHotelDropdownList,
  selectLocalCurrency,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
} from 'modules/dashboard/selectors';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import { KEYS, REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import {
  generateSummaryShareUrl,
  getAlignmentAndDateComparisonSchema,
  getBookingDateSchema,
  getChartBreakdownFilterList,
  getComparisonStayDateSchema,
  getComparisonBookingDateSchema,
  getDashboardPmsSyncSchema,
  getFarFieldPmsSyncSchema,
  getGroupASchema,
  getGroupHotelFilterSchema,
  getHotelGroupASchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getIsForecastSchema,
  getNormalizedViewSchema,
  getPaceHorizonSchema,
  getPaceVariableSchema,
  getPmsSyncSchema,
  getRevenueForecastSchema,
  getRoomForecastSchema,
  getSymmetricComparisonSchema,
  getTargetDateSchema,
  getMaxStayDateSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import writeLog from 'modules/common/utils/filter-log';
import { ENVIRONMENT } from 'config';
import calculateComparisonDates, {
  hasLeapDay,
} from '../../../../../common/utils/dateComparisonUtils';

/**
 * Custom hook to generate summary widget filter schemas
 */
const useSummaryWidget = () => {
  const dispatch = useDispatch();
  //
  const summaryForecastFilter = useSelector(selectSummaryForecastFilter);
  const chartBreakdown = useSelector(selectBreakdown);
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const bookingDate = useSelector(selectSummaryBookingDate);
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const paceVariable = useSelector(selectPaceVariable);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const isDetailed = useSelector(selectIsDetailed);
  const endDate = useSelector(selectSummaryEndDate);
  const startDate = useSelector(selectSummaryStartDate);
  const customBooking = useSelector(selectSummaryOtb);
  const alignmentToggle = useSelector(selectSummaryAlignmentToggle);
  const customDate = useSelector(selectSummaryCustomDate);
  const visualToggle = useSelector(selectSummaryVisualToggle);
  const paceVisualIsDisplay = useSelector(selectPaceVisualIsDisplay);
  const revenueSetIsDisplay = useSelector(selectRevenueSetIsDisplay);
  const cxlSetIsDisplay = useSelector(selectCxlSetIsDisplay);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const forecastToggle = useSelector(selectSummaryForecastFilter);
  const reportType = useSelector(selectReportType);
  const summaryFilterList = useSelector(selectSummaryFilterList);
  const paceVisualFilterList = useSelector(selectPaceVisualFilterList);
  const hotelGroupsDropdownList = useSelector(selectHotelGroupDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelDropdownList = useSelector(selectHotelDropdownList);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const localCurrency = useSelector(selectLocalCurrency);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  //
  const [previousHotel, setPreviousHotel] = useState(null);
  //
  const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
  const persistValues = Object.values(serializedState)[1];
  const persistedHotelName = JSON.parse(persistValues).hotelName;
  //
  const handleLogging = (message) => {
    if (ENVIRONMENT.PBI_EMBED_DEBUG_MODE?.toLowerCase() === 'true') {
      console.log(message);
    }
  };
  //
  useEffect(() => {
    // Persist hotel focus on value on browser refresh
    setPreviousHotel(persistedHotelName);
    if (previousHotel !== null) {
      // dispatch(summaryActions.setHotelFocusOn(hotelDropdownList[0]));
    }
  }, [hotelId]);

  // Triggered when pmsSyncToggle,chartBreakdown,bookingDate,visualFilters,dateComparison,alignment,targetDate,hotelId,symmetricComparisonToggle,isDetailed,endDate,startDate value changes
  useEffect(() => {
    if (
      !dateComparison ||
      !chartBreakdown ||
      !alignment ||
      !bookingDate ||
      !targetDate ||
      !visualFilters ||
      !hotelId ||
      (dateComparison && dateComparison === KEYS.CUSTOM && endDate === null)
    )
      return;
    // Generate filter list for summary widget top 8 visuals
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    let hotelListIds = [];
    let hotelGroup1Filter;

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode]);
    }
    //
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode]);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    //
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const symmetricComparisonFilter = getSymmetricComparisonSchema(
      visualFilters,
      symmetricComparisonToggle
    );
    const chartBreakdownFilter = getChartBreakdownFilterList(
      visualFilters,
      chartBreakdown,
      isDetailed
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.SUMMARY);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const summaryPmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    const start = performance.now();
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    const end = performance.now();
    handleLogging(`${new Date().toISOString()} - Date calculation duration: ${end - start} ms`);
    //
    const hasLeap = hasLeapDay(adjustedStayDate?.startDate, adjustedStayDate?.endDate);
    dispatch(summaryActions.setIsSummaryLeapComparison(hasLeap));
    //
    if (
      chartBreakdownFilter &&
      bookingDateFilter &&
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      symmetricComparisonFilter &&
      normalizedFilter &&
      summaryPmsSyncSchema
    ) {
      const filters = [
        ...summaryPmsSyncSchema,
        ...pmsSyncFilter,
        chartBreakdownFilter,
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        symmetricComparisonFilter,
        ...hotelFilters,
        normalizedFilter,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      //
      if (
        JSON.stringify(filters) !== JSON.stringify(summaryFilterList) &&
        reportType === REPORT_TYPE.SUMMARY
      ) {
        //
        const dispatchTime = performance.now();
        handleLogging(
          `${new Date().toISOString()} - Dispatched updateSummaryFilterList after ${
            dispatchTime - start
          } ms`
        );
        writeLog('Summary Filter', filters);
        dispatch(summaryActions.updateSummaryFilterList(filters));
      }
    } else dispatch(summaryActions.updateSummaryFilterList([]));
  }, [
    pmsSyncToggle,
    chartBreakdown,
    bookingDate,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    symmetricComparisonToggle,
    isDetailed,
    endDate,
    startDate,
    enableHotelGroups,
    reportType,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    hotelGroupsDropdownList,
    normalizedViewToggle,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
    resetBookingDate,
  ]);

  // Triggered when pmsSyncToggle,visualFilters,dateComparison,alignment,targetDate,hotelId,paceHorizon,paceVariable,endDate,startDate value changes
  useEffect(() => {
    if (
      reportType !== REPORT_TYPE.SUMMARY ||
      !dateComparison ||
      !alignment ||
      !targetDate ||
      !visualFilters ||
      !hotelId ||
      !paceHorizon ||
      !paceVariable ||
      (dateComparison && dateComparison === KEYS.CUSTOM && endDate === null)
    )
      return;
    // Generate filter list for summary widget pace visual
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    let hotelListIds = [];
    let hotelGroup1Filter;

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    const groupHotelFilter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotelFilter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotelFilter);
    }
    //
    const groupAFilter = getGroupASchema(visualFilters);
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds, groupAFilter);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupAFilter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    if ((enableSecondaryHotelGroups || enableHotelGroups) && dimHotelIds?.length === 0) {
      dimHotelIds = new Set(hotelDropdownList?.map((filter) => filter?.hotelCode));
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    //
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.SUMMARY);
    const paceHorizonFilter = getPaceHorizonSchema(visualFilters, paceHorizon);
    const paceVariableFilter = getPaceVariableSchema(
      visualFilters,
      paceVariable,
      TAGS.SUMMARY_PACE_VARIABLE
    );
    const isForecastFilter = getIsForecastSchema(visualFilters, summaryForecastFilter);
    const roomForecastFilter = getRoomForecastSchema(visualFilters, pmsSyncToggle);
    const revenueForecastFilter = getRevenueForecastSchema(visualFilters, pmsSyncToggle);
    const farFieldPmsFilter = getFarFieldPmsSyncSchema(visualFilters, pmsSyncToggle);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const summaryPmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const { adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    //
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    //
    if (
      targetDateFilter &&
      paceHorizonFilter &&
      paceVariableFilter &&
      roomForecastFilter &&
      revenueForecastFilter &&
      alignmentAndDateComparisonFilter &&
      farFieldPmsFilter &&
      isForecastFilter &&
      normalizedFilter &&
      summaryPmsSyncSchema
    ) {
      const filters = [
        ...pmsSyncFilter,
        ...summaryPmsSyncSchema,
        targetDateFilter,
        paceHorizonFilter,
        paceVariableFilter,
        roomForecastFilter,
        revenueForecastFilter,
        ...alignmentAndDateComparisonFilter,
        ...farFieldPmsFilter,
        isForecastFilter,
        ...hotelFilters,
        normalizedFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (
        JSON.stringify(filters) !== JSON.stringify(paceVisualFilterList) &&
        reportType === REPORT_TYPE.SUMMARY
      ) {
        //
        writeLog('Pace Visuals Filter', filters);
        dispatch(summaryActions.setPaceVisualFilterList(filters));
      }
    }
  }, [
    pmsSyncToggle,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    paceHorizon,
    paceVariable,
    endDate,
    startDate,
    visualToggle,
    enableHotelGroups,
    summaryForecastFilter,
    reportType,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    hotelGroupsDropdownList,
    normalizedViewToggle,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
  ]);

  // Triggers when filter values dependencies changes
  useEffect(() => {
    if (
      !dateComparison &&
      !chartBreakdown &&
      !bookingDate &&
      !targetDate &&
      !hotelId &&
      !paceHorizon &&
      !paceVariable
    )
      return;
    // Generate summary widget filter url
    const summaryUrl = generateSummaryShareUrl(
      pmsSyncToggle,
      hotelId,
      targetDate,
      dateComparison,
      paceVariable,
      visualFilters,
      paceHorizon,
      startDate,
      endDate,
      customDate,
      isDetailed,
      chartBreakdown,
      customBooking,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      hotelId,
      enableHotelGroups,
      visualToggle,
      paceVisualIsDisplay,
      revenueSetIsDisplay,
      cxlSetIsDisplay,
      symmetricComparisonIsDisplay,
      forecastToggle,
      enableSecondaryHotelGroups,
      hotelFocusOn,
      normalizedViewToggle,
      localCurrency
    );
    //
    dispatch(summaryActions.setSummaryUrl(summaryUrl));
  }, [
    pmsSyncToggle,
    chartBreakdown,
    bookingDate,
    visualFilters,
    dateComparison,
    alignmentToggle,
    targetDate,
    hotelId,
    paceHorizon,
    paceVariable,
    symmetricComparisonToggle,
    isDetailed,
    customBooking,
    startDate,
    endDate,
    customDate,
    enableHotelGroups,
    visualToggle,
    paceVisualIsDisplay,
    revenueSetIsDisplay,
    cxlSetIsDisplay,
    symmetricComparisonIsDisplay,
    forecastToggle,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    normalizedViewToggle,
    localCurrency,
  ]);
};
//
export default useSummaryWidget;
